import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, Trash2, FolderOpen, HardDrive, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetStorageCollectionsQuery,
  useGetStorageSourcesQuery,
  useCreateStorageCollectionMutation,
  useUpdateStorageCollectionMutation,
  useDeleteStorageCollectionMutation,
} from '@/store/api/apiSlice';
import { useCreateCollectionMarkdownMutation } from '@/store/api/storageContentApi';
import type { StorageCollection, StorageSource } from '@/types';
import { format } from 'date-fns';
import { buildStorageCollectionPayload } from '@/lib/storage-collection-payload';
import { useToast } from '@/components/shared/Toast';
import { wsClient } from '@/lib/ws';
import { StorageEventType, STORAGE_UPDATES_ROOM } from '@cluster/types';

interface CollectionFormData {
  name: string;
  description: string;
  sourceIds: string[];
  defaultSourceId: string;
  versioningEnabled: boolean;
  softDeleteDays: number;
  maxConcurrentJobs: number;
  maxFileSizeMB: number;
  maxTotalSizeMB: number;
}

const defaultFormData: CollectionFormData = {
  name: '',
  description: '',
  sourceIds: [],
  defaultSourceId: '',
  versioningEnabled: false,
  softDeleteDays: 30,
  maxConcurrentJobs: 3,
  maxFileSizeMB: 0,
  maxTotalSizeMB: 0,
};

const SOFT_DELETE_PRESETS = [
  { label: 'None (permanent)', value: 0 },
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: '365 days', value: 365 },
];

export function StorageCollectionsPage() {
  const navigate = useNavigate();
  const { error: toastError } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCollection, setEditingCollection] = useState<StorageCollection | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<StorageCollection | null>(null);
  const [formData, setFormData] = useState<CollectionFormData>(defaultFormData);
  const [markdownOpen, setMarkdownOpen] = useState(false);
  const [markdownForm, setMarkdownForm] = useState({
    collectionId: '',
    name: '',
    description: '',
    content: '',
  });

  const { data: collections, isLoading, refetch: refetchCollections } = useGetStorageCollectionsQuery();
  const { data: sources } = useGetStorageSourcesQuery();
  const [createCollection, { isLoading: isCreating }] = useCreateStorageCollectionMutation();
  const [updateCollection, { isLoading: isUpdating }] = useUpdateStorageCollectionMutation();
  const [deleteCollection, { isLoading: isDeleting }] = useDeleteStorageCollectionMutation();
  const [createMarkdown, { isLoading: isCreatingMarkdown }] = useCreateCollectionMarkdownMutation();

  // Use ref to access refetch without causing the WS-effect to re-run
  const refetchCollectionsRef = useRef(refetchCollections);
  useEffect(() => {
    refetchCollectionsRef.current = refetchCollections;
  }, [refetchCollections]);

  // WebSocket real-time refresh (no polling)
  useEffect(() => {
    const onCollectionEvent = () => { refetchCollectionsRef.current(); };

    const unsubConnected = wsClient.on('connected', () => {
      wsClient.subscribe(STORAGE_UPDATES_ROOM).catch(() => {});
    });

    if (!wsClient.connected) {
      wsClient.connect().catch(() => {});
    } else {
      wsClient.subscribe(STORAGE_UPDATES_ROOM).catch(() => {});
    }

    const unsubs = [
      unsubConnected,
      wsClient.on(StorageEventType.UPLOAD_COMPLETED, onCollectionEvent),
      wsClient.on(StorageEventType.UPLOAD_FAILED, onCollectionEvent),
      wsClient.on(StorageEventType.FILE_DELETED, onCollectionEvent),
      wsClient.on(StorageEventType.REPLICATION_STATUS, onCollectionEvent),
    ];

    return () => {
      unsubs.forEach((u) => u());
      wsClient.unsubscribe(STORAGE_UPDATES_ROOM).catch(() => {});
    };
  }, []);

  const collectionList = (collections as StorageCollection[] | undefined) ?? [];
  const sourceList = (sources as StorageSource[] | undefined) ?? [];

  const columns: ColumnDef<StorageCollection, unknown>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="font-medium">{row.original.name}</span>
        </div>
      ),
    },
    {
      accessorKey: 'sources',
      header: 'Sources',
      cell: ({ row }) => (
        <div className="flex gap-1 flex-wrap">
          {(row.original.sources ?? []).map((s) => {
            const src = sourceList.find((x) => x._id === s.sourceId);
            return (
              <Badge key={s.sourceId} variant="outline" className="text-xs gap-1">
                <HardDrive className="h-3 w-3" />
                {src?.name ?? s.sourceId.slice(0, 8)}
                {s.sourceId === row.original.defaultSourceId && ' ★'}
              </Badge>
            );
          })}
        </div>
      ),
    },
    {
      accessorKey: 'settings.versioningEnabled',
      header: 'Versioning',
      cell: ({ row }) => (
        <Badge variant={row.original.settings.versioningEnabled ? 'default' : 'secondary'} className="text-xs">
          {row.original.settings.versioningEnabled ? 'On' : 'Off'}
        </Badge>
      ),
    },
    {
      accessorKey: 'settings.softDeleteDays',
      header: 'Soft Delete',
      cell: ({ row }) => (
        <span className="text-sm">
          {row.original.settings.softDeleteDays === 0 ? 'None' : `${row.original.settings.softDeleteDays}d`}
        </span>
      ),
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      cell: ({ row }) => format(new Date(row.original.createdAt), 'MMM d, yyyy'),
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEdit(row.original); }}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ];

  function openCreate() {
    setEditingCollection(null);
    setFormData({ ...defaultFormData });
    setModalOpen(true);
  }

  function openEdit(col: StorageCollection) {
    setEditingCollection(col);
    setFormData({
      name: col.name,
      description: col.description ?? '',
      sourceIds: (col.sources ?? []).map((s) => s.sourceId),
      defaultSourceId: col.defaultSourceId,
      versioningEnabled: col.settings.versioningEnabled,
      softDeleteDays: col.settings.softDeleteDays,
      maxConcurrentJobs: col.settings.maxConcurrentJobs,
      maxFileSizeMB: col.settings.maxFileSizeBytes
        ? Math.round(col.settings.maxFileSizeBytes / (1024 * 1024))
        : 0,
      maxTotalSizeMB: col.settings.maxTotalSizeBytes
        ? Math.round(col.settings.maxTotalSizeBytes / (1024 * 1024))
        : 0,
    });
    setModalOpen(true);
  }

  function toggleSourceId(id: string) {
    setFormData((prev) => {
      const ids = prev.sourceIds.includes(id)
        ? prev.sourceIds.filter((s) => s !== id)
        : [...prev.sourceIds, id];
      const defaultSourceId = ids.includes(prev.defaultSourceId)
        ? prev.defaultSourceId
        : ids[0] ?? '';
      return { ...prev, sourceIds: ids, defaultSourceId };
    });
  }

  async function handleSave() {
    // Client-side required-field validation
    const missing: string[] = [];
    if (!formData.name.trim()) missing.push('name');
    if (formData.sourceIds.length === 0) missing.push('sourceIds');

    if (missing.length > 0) {
      toastError(`Faltan campos obligatorios: ${missing.join(', ')}`);
      return;
    }

    const payload = buildStorageCollectionPayload(formData);

    try {
      if (editingCollection) {
        await updateCollection({ id: editingCollection._id, body: payload }).unwrap();
      } else {
        await createCollection(payload).unwrap();
      }
      setModalOpen(false);
    } catch (err) {
      const data = (err as {
        data?: {
          error?: { message?: string; details?: Array<{ field?: string; message?: string }> };
          message?: string;
          details?: Array<{ field?: string; message?: string }>;
        };
      })?.data;
      const details = data?.error?.details ?? data?.details;
      let msg =
        data?.error?.message ??
        data?.message ??
        'No se pudo guardar la colección de almacenamiento. Inténtalo de nuevo.';
      if (Array.isArray(details) && details.length > 0) {
        msg = details
          .map((d) => (d.field ? `${d.field}: ${d.message ?? ''}` : d.message))
          .filter(Boolean)
          .join(', ');
      }
      toastError(msg);
    }
  }

  async function handleDelete() {
    if (deleteTarget) {
      await deleteCollection(deleteTarget._id).unwrap();
      setDeleteTarget(null);
    }
  }

  async function handleCreateMarkdown() {
    if (!markdownForm.collectionId) {
      toastError('Select a collection');
      return;
    }
    if (!markdownForm.name.trim()) {
      toastError('Markdown file name is required');
      return;
    }
    if (!markdownForm.content.trim()) {
      toastError('Content is empty');
      return;
    }
    try {
      await createMarkdown({
        collectionId: markdownForm.collectionId,
        body: {
          name: markdownForm.name.trim().endsWith('.md') ? markdownForm.name.trim() : `${markdownForm.name.trim()}.md`,
          content: markdownForm.content,
          description: markdownForm.description.trim() || undefined,
        },
      }).unwrap();
      setMarkdownOpen(false);
      setMarkdownForm({ collectionId: '', name: '', description: '', content: '' });
    } catch (err) {
      toastError(
        (err as { data?: { error?: { message?: string }; message?: string } })?.data?.error?.message ??
          (err as { data?: { message?: string } })?.data?.message ??
          'No se pudo crear el markdown.',
      );
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Storage Collections</h1>
          <p className="text-sm text-muted-foreground">
            Manage file collections and their storage settings
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { setMarkdownOpen(true); setMarkdownForm({ collectionId: collectionList[0]?._id ?? '', name: '', description: '', content: '' }); }}>
            <FileText className="mr-2 h-4 w-4" />
            New Markdown
          </Button>
          <Button onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Collection
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <p className="text-sm text-muted-foreground">
            Click a collection to browse its files
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : collectionList.length === 0 ? (
            <EmptyState
              title="No collections"
              description="Create a collection to start storing files"
              action={<Button onClick={openCreate}>Add Collection</Button>}
            />
          ) : (
            <DataTable
              columns={columns}
              data={collectionList}
              onRowClick={(row) => navigate(`/storage/collections/${row._id}/files`)}
            />
          )}
        </CardContent>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingCollection ? 'Edit Collection' : 'Add Collection'}
            </DialogTitle>
            <DialogDescription>
              {editingCollection
                ? 'Update collection configuration.'
                : 'Create a new file collection.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                placeholder="my-collection"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <textarea
                id="description"
                className="flex min-h-[70px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                placeholder="What is stored in this collection?"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            {/* Source selection */}
            <div className="space-y-2 rounded-lg border p-3">
              <p className="text-sm font-medium">Sources</p>
              {sourceList.length === 0 ? (
                <p className="text-xs text-muted-foreground">No sources available. Add a source first.</p>
              ) : (
                <div className="space-y-2">
                  {sourceList.map((src) => (
                    <label key={src._id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.sourceIds.includes(src._id)}
                        onChange={() => toggleSourceId(src._id)}
                      />
                      <span>{src.name}</span>
                      <Badge variant="outline" className="text-xs">{src.type}</Badge>
                    </label>
                  ))}
                </div>
              )}
              {formData.sourceIds.length > 0 && (
                <div className="space-y-1 pt-2 border-t">
                  <Label className="text-xs">Default source</Label>
                  <select
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                    value={formData.defaultSourceId}
                    onChange={(e) => setFormData({ ...formData, defaultSourceId: e.target.value })}
                  >
                    {formData.sourceIds.map((sid) => {
                      const src = sourceList.find((s) => s._id === sid);
                      return (
                        <option key={sid} value={sid}>{src?.name ?? sid}</option>
                      );
                    })}
                  </select>
                </div>
              )}
            </div>

            {/* Settings cards */}
            <div className="space-y-3 rounded-lg border p-3">
              <CardTitle className="text-sm">Settings</CardTitle>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="versioning"
                  checked={formData.versioningEnabled}
                  onChange={(e) => setFormData({ ...formData, versioningEnabled: e.target.checked })}
                />
                <Label htmlFor="versioning" className="text-sm">Enable versioning</Label>
              </div>

              <div className="space-y-1">
                <Label className="text-xs">Soft-delete retention</Label>
                <div className="flex gap-2 flex-wrap">
                  {SOFT_DELETE_PRESETS.map((preset) => (
                    <Button
                      key={preset.value}
                      type="button"
                      variant={formData.softDeleteDays === preset.value ? 'default' : 'outline'}
                      size="sm"
                      className="text-xs"
                      onClick={() => setFormData({ ...formData, softDeleteDays: preset.value })}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>
                {formData.softDeleteDays > 0 && (
                  <div className="flex items-center gap-2 mt-1">
                    <Label htmlFor="customDays" className="text-xs">Custom:</Label>
                    <Input
                      id="customDays"
                      type="number"
                      min={1}
                      max={364}
                      className="w-20 h-8 text-xs"
                      value={formData.softDeleteDays}
                      onChange={(e) => setFormData({ ...formData, softDeleteDays: Math.min(364, Math.max(1, Number(e.target.value))) })}
                    />
                    <span className="text-xs text-muted-foreground">days</span>
                  </div>
                )}
              </div>

              <div className="space-y-1">
                <Label htmlFor="maxJobs" className="text-xs">Max concurrent jobs</Label>
                <Input
                  id="maxJobs"
                  type="number"
                  min={1}
                  max={20}
                  value={formData.maxConcurrentJobs}
                  onChange={(e) => setFormData({ ...formData, maxConcurrentJobs: Number(e.target.value) })}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="maxFileSize" className="text-xs">Max file size (MB, 0 = unlimited)</Label>
                <Input
                  id="maxFileSize"
                  type="number"
                  min={0}
                  value={formData.maxFileSizeMB}
                  onChange={(e) => setFormData({ ...formData, maxFileSizeMB: Number(e.target.value) })}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="maxTotalSize" className="text-xs">Max total size (MB, 0 = unlimited)</Label>
                <Input
                  id="maxTotalSize"
                  type="number"
                  min={0}
                  value={formData.maxTotalSizeMB}
                  onChange={(e) => setFormData({ ...formData, maxTotalSizeMB: Number(e.target.value) })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={isCreating || isUpdating}>
              {(isCreating || isUpdating) ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Markdown dialog */}
      <Dialog open={markdownOpen} onOpenChange={setMarkdownOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Markdown</DialogTitle>
            <DialogDescription>
              Create a .md file in the selected collection (POST /storage/collections/:id/markdown).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="mdCollection">Collection</Label>
              <select
                id="mdCollection"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={markdownForm.collectionId}
                onChange={(e) => setMarkdownForm({ ...markdownForm, collectionId: e.target.value })}
              >
                <option value="">— select collection —</option>
                {collectionList.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="mdName">File name</Label>
                <Input
                  id="mdName"
                  placeholder="readme.md"
                  value={markdownForm.name}
                  onChange={(e) => setMarkdownForm({ ...markdownForm, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mdDescription">Description</Label>
                <Input
                  id="mdDescription"
                  value={markdownForm.description}
                  onChange={(e) => setMarkdownForm({ ...markdownForm, description: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mdContent">Content</Label>
              <textarea
                id="mdContent"
                rows={8}
                className="flex min-h-[140px] w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
                placeholder="# Title&#10;&#10;Write markdown here…"
                value={markdownForm.content}
                onChange={(e) => setMarkdownForm({ ...markdownForm, content: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkdownOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateMarkdown} disabled={isCreatingMarkdown}>
              {isCreatingMarkdown ? 'Creating…' : 'Create Markdown'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete Collection"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? All files in this collection will be affected. This action cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={isDeleting}
      />
    </div>
  );
}
